import {
    Button,
    Card,
    Col,
    DatePicker, Divider, Dropdown, Grid, Empty,
    Form,
    Input,
    InputNumber, Menu,
    Popover,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag, Tooltip,
    Typography
} from "antd";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import ApiService from "../../core/service/ApiService";
import { v4 } from "uuid";
import Helper from "../../core/Helper";
import moment from "moment";
import FormDownload from "./FormDownload";
import { useModal } from "../../core/context/ModalContext";
import StateChanger from "./StateChanger";
import AppointmentFiles from "./files/AppointmentFiles";
import { EyeOutlined, SmallDashOutlined } from "@ant-design/icons";
import AppointmentTypeSelect from "./AppointmentTypeSelect";
import CurrencySelect from "../../core/components/CurrencySelect";
import { Currency } from "../Payment/MultiplePaymentApp";
import InstitutionCreateEdit from "../institution/InstitutionCreateEdit";
import AppointmentOperationSubItemsEdit from "./AppointmentOperationSubItemsEdit";
import { ColorDot } from "../../core/components/ColorDot";
import AppConfig from "../../core/service/AppConfig";
import Borcodeprinter from "./BarcodePrinter";

const { Text, Title } = Typography;

export default function AppointmentDetailEdit({ appointmentId, onAppointmentChange, onSave, onDelete, onCancel, onPayment, onCheckedChange, onStateChanged }) {
    const formRef = useRef()
    const formOtherOperationRef = useRef();
    const screens = Grid.useBreakpoint();
    const [doctors, setDoctors] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const { firmSettings } = useUser();
    const [operationList, setOperationList] = useState([]);
    const { openModal, closeModal } = useModal()
    const { hasPermission, user } = useUser();
    const [currency, setCurrency] = useState('TRY')
    const institutionIdRef = useRef(null)
    const appointmentTypeIdRef = useRef(null)

    const priceList = useRef([])
    const appointmentTypePriceList = useRef([])
    const appointmentTypes = useRef([])

    const [appointment, setAppointment] = useState({
        doctor: {},
        institution: {},
        unit: {},
        appointmentOperations: [],
        appointmentOtherOperations: [],
        checkIn: false,
        sendEnabiz: false,
    })

    const loadAppointment = async (appointmentId) => {
        if (!appointmentId)
            return

        let _appointment = {
            doctor: {},
            institution: {},
            unit: {},
            appointmentOperations: [],
            appointmentOtherOperations: [],
            startDate: moment(),
            endDate: moment().add(15, 'minutes')
        }

        if (typeof appointmentId === 'object') {
            _appointment = {
                ..._appointment,
                unit: {
                    id: appointmentId.unitId,
                    name: appointmentId.unitName,
                },
                firm: {
                    id: appointmentId.firmId,
                },
                newRecord: true,
                startDate: appointmentId.startDate,
                endDate: appointmentId.endDate,
            }
        }
        else {
            const res = await ApiService.Appointment.GetAppointment(appointmentId)
            if (res.success) {
                _appointment = res.data
                _appointment.startDate = moment(new Date(_appointment.startDate))
                _appointment.endDate = calcEndDate(_appointment.appointmentOperations, moment(new Date(_appointment.startDate)))
                _appointment.newRecord = false;
                _appointment.appointmentOperations = _appointment.appointmentOperations.map(x => {
                    return {
                        ...x,
                        subItems: JSON.parse(x.subItems ?? '[]')
                    }
                });

            }
        }

        let _doctors = []
        let _institutions = []
        let _operations = []
        await Promise.all([
            ApiService.Doctor.GetDoctorList({ unitId: _appointment?.unit?.id }).then(res => {
                _doctors = res.data
            }),
            ApiService.Institution.GetInstitutionListByFirm(_appointment?.firm?.id).then(res => {
                _institutions = res.data
            }),
            ApiService.Operation.GetOperationList(_appointment?.unit?.id).then(res => {
                _operations = res.data.map(x => ({
                    ...x,
                    subItems: JSON.parse(x.subItems ?? '[]')
                }))
            })
        ])

        const doctor = _doctors?.length > 0 && !_appointment?.doctor?.id ? _doctors[0] : _appointment.doctor
        const institution = _institutions?.length > 0 && !_appointment.institution.id ? (_institutions.find(x => x.isDefault) || _institutions[0]) : _appointment.institution
        const appointmentTypeId = _appointment.appointmentTypeId || institution?.appointmentType?.id

        await loadInstitutionPriceList(institution.id, _operations)
        await loadAppointmentTypePriceList(appointmentTypeId, _operations)

        setDoctors(_doctors)
        setInstitutions(_institutions)
        setOperationList(_operations)
        setAppointment({
            ..._appointment,
            doctor,
            institution,
            appointmentTypeId
        })
        setCurrency(_appointment.currency || 'TRY')

        formRef.current.setFieldsValue({
            doctorId: doctor?.id,
            institutionId: institution?.id,
            checkIn: false,
            anamnez: _appointment.anamnez,
            provisionNo: _appointment.provisionNo,
            note: _appointment.note,
            privateNote: _appointment.privateNote,
            startDate: _appointment.startDate,
            endDate: _appointment.endDate,
            appointmentTypeId: appointmentTypeId
        })
    }

    const calcEndDate = (operations, startDate) => {
        if (operations && operations.length > 0) {
            let sum = 0;
            operations.forEach(op => sum += op.processMinute)
            return moment(startDate).add(sum, 'minutes');
        }
        else {
            return moment(startDate).add(15, 'minutes');
        }
    }

    const onSelectOperation = async (id) => {
        const index = appointment.appointmentOperations.findIndex(x => x.operationId === id);
        if (index > -1)
            return;

        const operation = operationList.find(x => x.id === id);
        const institutionPrice = priceList.current.find(x => x.operationId === id)?.price
        const appTypePrice = appointmentTypePriceList.current.find(x => x.operationId === id)?.price
        const appType = appointmentTypes.current.find(x => x.id === appointment.appointmentTypeId)

        let price = operation?.price
        if (appType?.overrideInstitutionPrice) {
            price = appTypePrice
        }
        else if (institutionPrice) {
            price = institutionPrice
        }

        const ops = [
            ...appointment.appointmentOperations,
            {
                ...operation,
                id: v4(),
                operationId: operation.id,
                priceModified: false,
                price: price,
                institutionPrice: 300,
                discount: 0,
                subItems: [],
                operation
            }
        ]

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        })

        formRef.current.setFieldsValue({
            endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
        })
    }

    const getMaxDiscount = () => {
        const institution = institutions.find(x => x.id === appointment?.institution?.id);
        return Math.min(institution?.discount, user?.discount)
    }

    const updateOperationData = (id, key, value) => {
        const ops = [...appointment.appointmentOperations].map(x => {
            delete x.error;
            return x;
        });

        const index = ops.findIndex(x => x.id === id);
        if (index > -1) {
            const institutionPrice = priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === ops[index]?.operationId)?.price
            if (key === 'price') {
                if (institutionPrice > value) {
                    ops[index]['discount'] = 100 - (value * 100 / institutionPrice)
                }
                else {
                    ops[index]['discount'] = 0
                }
            }
            else if (key === 'discount') {
                if (value > 0) {
                    ops[index]['price'] = institutionPrice - (institutionPrice * value / 100)
                }
                else {
                    ops[index]['price'] = institutionPrice;
                }
            }

            ops[index][key] = value;
        }

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        });
    }

    const updateCurrency = (value) => {
        setCurrency(value)
        setAppointment({
            ...appointment,
            currency: value,
        });
    }

    const loadInstitutionPriceList = async (institutionId, operations) => {
        if (institutionIdRef.current === institutionId)
            return

        const res = await ApiService.Operation.GetInstitutionPriceList(institutionId, operations.map(x => x.id));
        if (res.success) {
            priceList.current = res.data
            institutionIdRef.current = institutionId
        }
    }

    const loadAppointmentTypePriceList = async (typeId, operations) => {
        if (appointmentTypeIdRef.current === typeId)
            return

        const res = await ApiService.Operation.GetAppointmentTypePriceList(typeId, operations.map(x => x.id));
        if (res.success) {
            appointmentTypePriceList.current = res.data
            appointmentTypeIdRef.current = typeId
        }
    }

    const deleteOtherOperation = (row) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: appointment.appointmentOtherOperations.filter(x => x.id !== row.id),
        })
    }

    const onOtherOperationFinish = (formData) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: [
                ...appointment.appointmentOtherOperations,
                {
                    id: v4(),
                    ...formData,
                }
            ],
        })
        formOtherOperationRef.current.resetFields()
    }

    const onStateChange = async (stateId, stateDescForm) => {
        formRef.current.setFieldsValue({
            appointmentStateId: stateId
        })

        setAppointment({
            ...appointment,
            state: {
                id: stateId
            },
            stateDesc: stateDescForm || {}
        })
    }

    const onFiles = () => {
        const component = (
            <AppointmentFiles appointmentId={appointmentId} />
        )
        openModal('', component, () => {
            onStateChanged && onStateChanged('files')
        })
    }

    const getInstitutionPrice = (operationId) => {
        return priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === operationId)?.price
    }

    const getAppointmentTypePrice = (operationId) => {
        return appointmentTypePriceList.current.find(x => x.appointmentTypeId === appointment.appointmentTypeId && x.operationId === operationId)?.price
    }

    const getInstitutionTotalPrice = () => {
        return priceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getAppointmentTypeTotalPrice = () => {
        return appointmentTypePriceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getTotalPrice = () => {
        return appointment?.appointmentOperations?.reduce((prev, val) => prev += val.price, 0)
    }

    const operationsPrice = appointment.appointmentOperations?.reduce((prev, val) => prev += val?.operation?.price, 0) || 0;
    const extraOperationsPrice = appointment.appointmentOtherOperations?.reduce((prev, val) => prev += val.price, 0) || 0;
    const institutionPrice = getInstitutionTotalPrice();
    const specialPrice = getAppointmentTypeTotalPrice();
    const grandTotal = getTotalPrice();
    const activeCurrency = currency || 'TRY';

    const renderSummaryContent = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontSize: 16 }}>{t('Total')}:</Text>
                        <Text strong style={{ color: '#1890ff', fontSize: 18 }}>{Helper.Currency.format(grandTotal, { currency: activeCurrency })}</Text>
                    </div>
                    <Space split={<Divider type="vertical" />} style={{ flexWrap: 'wrap' }}>
                        <Text>{t('Operations')}: <Text strong>{Helper.Currency.format(operationsPrice)}</Text></Text>
                        {extraOperationsPrice > 0 && (
                            <Text>{t('Extra')}: <Text strong>{Helper.Currency.format(extraOperationsPrice, { currency: activeCurrency })}</Text></Text>
                        )}
                        {(institutionPrice > 0 || specialPrice > 0) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('Institution')}: {Helper.Currency.format(institutionPrice)} / {t('Special')}: {Helper.Currency.format(specialPrice)}
                            </Text>
                        )}
                    </Space>
                </div>
            </div>
        )
    }

    const getPaymentSummary = (colSpan) => {
        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={colSpan}>
                        {renderSummaryContent()}
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        )
    }



    useEffect(() => {
        loadAppointment(appointmentId)
    }, [appointmentId]);

    useEffect(() => {
        if (appointment?.institution?.id) {
            loadInstitutionPriceList(appointment.institution.id, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.institution?.id]);

    useEffect(() => {
        if (appointment?.appointmentTypeId) {
            loadAppointmentTypePriceList(appointment?.appointmentTypeId, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.appointmentTypeId]);

    useEffect(() => {
        onAppointmentChange && onAppointmentChange(appointment)
    }, [appointment]);

    const renderActionButtons = () => (
        <>
            <Button.Group>
                {
                    appointmentId && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
                        <Popover
                            content={(
                                <Typography.Text>
                                    {t('Changes to the patient card will be saved.')}
                                </Typography.Text>
                            )}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    style={{ width: 60 }}
                                    size={'small'}
                                    onClick={async () => {
                                        await onSave({
                                            ...appointment,
                                            endDate: calcEndDate(appointment.appointmentOperations, moment(new Date(appointment.startDate)))
                                        })
                                        if (!appointment.newRecord)
                                            await loadAppointment(appointmentId)
                                    }}
                                >
                                    {t('Save')}
                                </Button>
                                {/* {
                                    appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && (
                                        <Button
                                            style={{ marginLeft: 4 }}
                                            size={'small'}
                                            type={'primary'}
                                            onClick={async () => {
                                                const res = await ApiService.AppointmentTeletip.SendEnabizBatch(appointmentId)
                                                if (res.success) {
                                                    await loadAppointment(appointmentId)
                                                }
                                            }}
                                        >
                                            {t('Send Enabiz')}
                                        </Button>
                                    )
                                } */}
                                <Borcodeprinter
                                    appointmentId={appointmentId} />
                            </div>
                        </Popover>
                    )
                }
            </Button.Group>
            {screens.md && <div style={{ flex: 1 }} />}
            <Button.Group style={{ flexWrap: 'wrap' }}>
                {
                    !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                        <Button size={'small'} onClick={onPayment} className={'bg-success text-white'}
                            style={{ width: 75 }}>{t('Payments')}</Button>
                    )
                }
                {
                    !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
                        <Button size={'small'} onClick={onFiles} className={'bg-success text-white'}
                            style={{ width: 75 }}>{t('Files')}</Button>
                    )
                }
                {
                    !(typeof appointmentId === "object") && (
                        <FormDownload
                            appointmentId={appointmentId}
                            unitId={appointment?.unit?.id}
                        />
                    )
                }
                {
                    !(typeof appointmentId === "object") && hasPermission(PERMISSIONS.APPOINTMENT_DELETE) && (
                        <Button
                            style={{ width: 60 }}
                            type={'primary'}
                            size={'small'}
                            danger={true}
                            onClick={async () => {
                                onDelete(appointment)
                            }}
                        >
                            {t('Delete')}
                        </Button>
                    )
                }
                {
                    appointmentId && (
                        <Button
                            style={{ width: 60 }}
                            type={'warning'}
                            size={'small'}
                            danger={true}
                            onClick={async () => {
                                onCancel()
                            }}
                        >
                            {t('Close')}
                        </Button>
                    )
                }
            </Button.Group>
        </>
    )

    const renderMobileOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                {appointment.appointmentOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Operations')} />
                )}
                {appointment.appointmentOperations.map(op => {
                    const ops = operationList.find(x => x.id === op.operationId)
                    return (
                        <Card size="small"
                            type="inner"
                            title={op?.operation?.name}
                            extra={
                                <Button
                                    size={'small'}
                                    danger
                                    disabled={
                                        Helper.Date.isBeforeToday(appointment.startDate) &&
                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                    }
                                    onClick={() => {
                                        setAppointment({
                                            ...appointment,
                                            appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== op.id),
                                        })
                                    }}>
                                    X
                                </Button>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                    <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{t('Accession / Min.')}</Typography.Text>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <Input
                                            style={{ width: 100 }}
                                            size={'small'}
                                            placeholder={t('Accession')}
                                            value={op.accessionNumber}
                                            onChange={(e) => {
                                                updateOperationData(op.id, 'accessionNumber', e.target.value)
                                            }}
                                        />
                                        <InputNumber
                                            style={{ width: 60 }}
                                            size={'small'}
                                            placeholder={t('Min')}
                                            defaultValue={op?.processMinute}
                                            value={op?.processMinute}
                                            onChange={(val) => {
                                                updateOperationData(op.id, 'processMinute', val)
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Typography.Text style={{ whiteSpace: 'nowrap' }}>{t('Price')}</Typography.Text>
                                            {ops && ops.subItems?.length > 0 && (
                                                <Button
                                                    icon={<SmallDashOutlined />}
                                                    size={'small'}
                                                    onClick={() => {
                                                        openModal(
                                                            t(''),
                                                            <AppointmentOperationSubItemsEdit
                                                                currency={currency}
                                                                subItems={ops.subItems}
                                                                selectedSubItems={op.subItems ?? []}
                                                                onSave={(items) => {
                                                                    const updatedOps = appointment.appointmentOperations.map(x => {
                                                                        if (x.id === op.id) {
                                                                            return {
                                                                                ...x,
                                                                                price: items.reduce((prev, val) => prev += val.price, 0),
                                                                                subItems: items
                                                                            }
                                                                        }
                                                                        return x
                                                                    })
                                                                    setAppointment({
                                                                        ...appointment,
                                                                        appointmentOperations: updatedOps
                                                                    })
                                                                    closeModal()
                                                                }}
                                                            />,
                                                            () => { }
                                                        )
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <TitleCurrencySelector
                                        value={currency}
                                        disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                        onChange={(e) => {
                                            updateCurrency(e)
                                        }}
                                    />
                                    <InputNumber
                                        disabled={
                                            Helper.Date.isBeforeToday(appointment.startDate) &&
                                            !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                        }
                                        style={{ width: 100 }}
                                        size={'small'}
                                        prefix={Currency[appointment.currency]}
                                        value={op?.price}
                                        step={0.01}
                                        onChange={(newPrice) => {
                                            if (ops?.subItems?.length > 0) {
                                                const updatedOperations = appointment.appointmentOperations.map(mapOp => {
                                                    if (mapOp.id === op.id) {
                                                        const subItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                                                        const ratio = newPrice / subItemsPrice

                                                        mapOp.price = newPrice
                                                        mapOp.subItems = mapOp.subItems.map(sub => ({
                                                            ...sub,
                                                            price: parseFloat((sub.price * ratio).toFixed(2))
                                                        }))

                                                        const newSubItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                                                        const diff = newPrice - newSubItemsPrice
                                                        if (Math.abs(diff) > 0.01 && mapOp.subItems.length > 0) {
                                                            mapOp.subItems[0].price = parseFloat((mapOp.subItems[0].price + diff).toFixed(2))
                                                        }

                                                        return { ...mapOp }
                                                    }
                                                    return mapOp
                                                })

                                                setAppointment({
                                                    ...appointment,
                                                    appointmentOperations: updatedOperations
                                                })
                                            } else {
                                                updateOperationData(op.id, 'price', newPrice)
                                            }
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                    <span style={{ color: '#888' }}>
                                        {Helper.Currency.format(op?.operation?.price)}
                                    </span>
                                    <span style={{ color: '#888' }}>
                                        {Helper.Currency.format(getInstitutionPrice(op?.operationId))} / {Helper.Currency.format(getAppointmentTypePrice(op?.operationId))}
                                    </span>
                                </div>

                            </div>
                        </Card>
                    )
                })}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    const renderMobileExtraOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {appointment.appointmentOtherOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Extra Operations')} />
                )}
                {appointment.appointmentOtherOperations.map((row, index) => (
                    <Card key={index} size="small" type="inner" title={row.name} extra={
                        <Button size={'small'} onClick={() => deleteOtherOperation(row)} danger={true}>X</Button>
                    }>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography.Text>{t('Price')}</Typography.Text>
                            <Typography.Text strong>{Helper.Currency.format(row.price, { currency })}</Typography.Text>
                        </div>
                    </Card>
                ))}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    return (
        <Card
            title={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography.Text>
                        {t('Appointment')} {typeof appointmentId !== 'object' ? appointmentId : ''}: {appointment.newRecord ? t('New') : t('Edit')} - {appointment?.unit?.name}
                    </Typography.Text>
                    {screens.md && renderActionButtons()}
                </div>
            )}
            style={{ backgroundColor: '#f5f5f5', height: '100%' }}>
            <Form
                layout={'vertical'}
                ref={formRef}
                onValuesChange={() => {
                    const form = formRef.current.getFieldsValue()

                    const ins = institutions.find(x => x.id === form.institutionId)
                    const doctor = doctors.find(x => x.id === form.doctorId)

                    let typeId = appointment.appointmentTypeId
                    if (form.institutionId !== appointment.institution.id) {
                        typeId = ins?.appointmentType?.id
                    }
                    else {
                        typeId = form.appointmentTypeId
                    }


                    setAppointment({
                        ...appointment,
                        institution: { ...ins },
                        doctor,
                        anamnez: form.anamnez,
                        provisionNo: form.provisionNo,
                        privateNote: form.privateNote,
                        note: form.note,
                        startDate: form.startDate,
                        endDate: calcEndDate(appointment.appointmentOperations, form.startDate),
                        appointmentTypeId: typeId
                    })

                    formRef.current.setFieldsValue({
                        endDate: calcEndDate(appointment.appointmentOperations, form.startDate),
                        appointmentTypeId: typeId
                    })
                }}
            >
                <Row gutter={24}>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Start Date')}
                            name={'startDate'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <DatePicker
                                size={'small'}
                                format={'DD-MM-YYYY HH:mm'}
                                style={{ width: '100%' }}
                                showTime={true}
                                locale={Helper.Date.getLocale()}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('End Date')}
                            name={'endDate'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <DatePicker
                                size={'small'}
                                format={'DD-MM-YYYY HH:mm'}
                                style={{ width: '100%' }}
                                showTime={true}
                                disabled={true}
                                locale={Helper.Date.getLocale()}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Doctor')}
                            name={'doctorId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                            >
                                {doctors.map(x => {
                                    return (
                                        <Select.Option
                                            key={x.id}
                                            value={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={(
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {t('Institution')}
                                    {(
                                        (() => {
                                            if (Helper.Date.isBeforeToday(appointment.startDate)) {
                                                if (hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        })()
                                    ) && (
                                            <Button
                                                size={'small'}
                                                icon={<EyeOutlined />}
                                                onClick={() => {
                                                    openModal(t(''), (
                                                        <InstitutionCreateEdit
                                                            showFirmSettings={false}
                                                            institutionId={appointment.institution.id}
                                                            editable={false}
                                                        />
                                                    ), async () => { })
                                                }}
                                            >

                                            </Button>
                                        )}
                                </div>
                            )}
                            name={'institutionId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                                showSearch={true}
                                disabled={
                                    Helper.Date.isBeforeToday(appointment.startDate) &&
                                    !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                }

                                filterOption={(input, option) => {
                                    return option.children.toLocaleLowerCase(firmSettings?.locale).includes(input.toLocaleLowerCase(firmSettings?.locale))
                                }}
                            >
                                {institutions.map(x => {
                                    return (
                                        <Select.Option
                                            value={x.id}
                                            key={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={t('Appointment Type')}
                            name={'appointmentTypeId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <AppointmentTypeSelect
                                onTypes={(types) => {
                                    appointmentTypes.current = types
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Provizyon No')}
                            name={'provisionNo'}
                        >
                            <Input size="small" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        {
                            (hasPermission(PERMISSIONS.VIEW_PRICE)) && (<Form.Item
                                label={t('Private Note')}
                                name={'privateNote'}
                            >
                                <Input size="small" />
                            </Form.Item>)
                        }
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={'Anamnez'}
                            name={'anamnez'}
                        >
                            <Input.TextArea
                                rows={3}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Appointment Note')}
                            name={'note'}
                        >
                            <Input.TextArea rows={3} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Form.Item>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Check-in')}</Typography.Text>
                                    <Switch checked={appointment.checkIn} onChange={(e) => {
                                        setAppointment({
                                            ...appointment,
                                            checkIn: e,
                                        })
                                        onCheckedChange && onCheckedChange(e)
                                    }} />
                                </div>
                            </Form.Item>
                            {/* {
                                appointment?.appointmentType?.enabizEnabled && (
                                    <Form.Item>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 8,
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Send E-Nabız')}</Typography.Text>
                                            <Switch checked={appointment.sendEnabiz} onChange={(e) => {
                                                setAppointment({
                                                    ...appointment,
                                                    sendEnabiz: e,
                                                })
                                            }} />
                                        </div>
                                    </Form.Item>
                                )
                            } */}
                            <Form.Item>
                                <StateChanger
                                    onStateChange={onStateChange}
                                    appointmentId={appointmentId}
                                    selectedStateId={appointment?.state?.id}
                                    checkIn={appointment.checkIn}
                                    onStates={(data) => {
                                        if (!data.find(x => x.id === appointment?.state?.id)) {
                                            setAppointment({
                                                ...appointment,
                                                state: {
                                                    id: data.find(x => x.stateGroup?.id === 1)?.id
                                                }
                                            })
                                        }
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </Col>
                    <Col xs={12}>
                    </Col>
                </Row>
            </Form>
            <Card
                style={{ backgroundColor: '#fafafa' }}
            >
                <div style={{ alignItems: 'start', gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                        <Tabs
                            size={'small'}
                            tabPosition={'top'}
                        >
                            <Tabs.TabPane
                                tab={'Operations'}
                                key={'operations'}
                            >
                                <Select
                                    style={{ width: '100%' }}
                                    size={'small'}
                                    id={'operation-id-list'}
                                    autoClearSearchValue={true}
                                    placeholder={t('Select Operation')}
                                    disabled={
                                        Helper.Date.isBeforeToday(appointment.startDate) &&
                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                    }
                                    showSearch={true}
                                    filterOption={(input, option) => {
                                        return Helper.Text.normalizeText(option.children.toString()).includes(Helper.Text.normalizeText(input))
                                    }}
                                    mode={'multiple'}
                                    value={appointment?.appointmentOperations?.map(x => x.operationId)}
                                    onBlur={() => {
                                        document.getElementsByClassName('operation-list-dropdown')[0].style.display = 'none'
                                    }}
                                    onClick={(e) => {
                                        document.getElementsByClassName('operation-list-dropdown')[0].style.display = ''
                                    }}
                                    dropdownClassName={'operation-list-dropdown'}
                                    onSelect={onSelectOperation}
                                    tagRender={(props) => (
                                        <Tag
                                            closable={true}
                                            style={{ marginRight: 3, whiteSpace: 'normal', textAlign: 'left', wordBreak: 'break-word', height: 'auto', maxWidth: '100%', display: 'inline-flex', alignItems: 'center' }}
                                            onClose={(e) => {
                                                if (Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) {
                                                    e.preventDefault();
                                                    return;
                                                }

                                                const ops = appointment?.appointmentOperations?.filter(x => x.operationId !== props.value);
                                                setAppointment({
                                                    ...appointment,
                                                    appointmentOperations: ops
                                                });

                                                formRef.current.setFieldsValue({
                                                    endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
                                                });
                                            }}
                                        >
                                            {props.label}
                                        </Tag>
                                    )}
                                >
                                    {
                                        operationList.map(x => {
                                            return (
                                                <Select.Option key={x.id} value={x.id}>
                                                    {`${x.name} - ${x.processMinute} ${t('minute')}`}
                                                </Select.Option>
                                            )
                                        })
                                    }
                                </Select>

                                <div>
                                    {!screens.md ? renderMobileOperations() : (
                                        <Table
                                            style={{ marginTop: 8 }}
                                            scroll={{ x: 600 }}
                                            size={'small'}
                                            dataSource={appointment.appointmentOperations}
                                            pagination={false}
                                            rowKey={'id'}
                                            columns={[
                                                {
                                                    key: 'name',
                                                    dataIndex: 'name',
                                                    title: t('Name'),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        if (ops && ops.subItems?.length > 0) {
                                                            return (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 8
                                                                }}>
                                                                    <Tooltip
                                                                        title={(
                                                                            <>
                                                                                {row?.subItems?.length === 0 && t('No Sub Operations')}
                                                                                <table>
                                                                                    {
                                                                                        row?.subItems?.map(subItem => (
                                                                                            <tr>
                                                                                                <td>{subItem.name}</td>
                                                                                                <td>
                                                                                                    <ColorDot />
                                                                                                </td>
                                                                                                <td>{Helper.Currency.format(subItem.price, { currency })}</td>
                                                                                            </tr>
                                                                                        ))
                                                                                    }
                                                                                </table>
                                                                            </>
                                                                        )}
                                                                    >
                                                                        {row?.operation?.name}
                                                                    </Tooltip>
                                                                    <Button
                                                                        icon={<SmallDashOutlined />}
                                                                        size={'small'}
                                                                        onClick={() => {
                                                                            openModal(
                                                                                t(''),
                                                                                <AppointmentOperationSubItemsEdit
                                                                                    currency={currency}
                                                                                    subItems={ops.subItems}
                                                                                    selectedSubItems={row.subItems ?? []}
                                                                                    onSave={(items) => {
                                                                                        const ops = appointment.appointmentOperations.map(x => {
                                                                                            if (x.id === row.id) {
                                                                                                return {
                                                                                                    ...x,
                                                                                                    price: items.reduce((prev, val) => prev += val.price, 0),
                                                                                                    subItems: items
                                                                                                }
                                                                                            }
                                                                                            return x
                                                                                        })
                                                                                        setAppointment({
                                                                                            ...appointment,
                                                                                            appointmentOperations: ops
                                                                                        })
                                                                                        closeModal()
                                                                                    }}
                                                                                />,
                                                                                () => { }
                                                                            )
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                        return row?.operation?.name
                                                    }
                                                },
                                                {
                                                    key: 'accessionNumber',
                                                    dataIndex: 'accessionNumber',
                                                    title: (<>{t('Accession Number')} / <br /> {t('Process Minute')}</>),
                                                    width: 225,
                                                    render: (text, row) => (
                                                        <div>
                                                            <Input
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                value={text}
                                                                onChange={(e) => {
                                                                    updateOperationData(row.id, 'accessionNumber', e.target.value)
                                                                }}
                                                            />
                                                            <InputNumber
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                defaultValue={row?.processMinute}
                                                                value={row?.processMinute}
                                                                onChange={(val) => {
                                                                    updateOperationData(row.id, 'processMinute', val)
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'newPrice',
                                                    dataIndex: 'newPrice',
                                                    width: 300,
                                                    title: (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            {t('Patient Op. Price')}
                                                            <TitleCurrencySelector
                                                                value={currency}
                                                                disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                                                onChange={(e) => {
                                                                    updateCurrency(e)
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        return (
                                                            <div>
                                                                <InputNumber
                                                                    // disabled={ops?.subItems?.length > 0}
                                                                    disabled={
                                                                        Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                                                    }
                                                                    style={{ width: '100%' }}
                                                                    size={'small'}
                                                                    prefix={Currency[appointment.currency]}
                                                                    value={row?.price}
                                                                    step={0.01}
                                                                    onChange={(newPrice) => {
                                                                        if (ops?.subItems?.length > 0) {
                                                                            const updatedOperations = appointment.appointmentOperations.map(op => {
                                                                                if (op.id === row.id) {
                                                                                    const subItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const ratio = newPrice / subItemsPrice

                                                                                    op.price = newPrice
                                                                                    op.subItems = op.subItems.map(sub => ({
                                                                                        ...sub,
                                                                                        price: parseFloat((sub.price * ratio).toFixed(2))
                                                                                    }))

                                                                                    // Alt işlemler toplamı ile yeni fiyat arasında ufak fark varsa kapat
                                                                                    const newSubItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const diff = newPrice - newSubItemsPrice
                                                                                    if (Math.abs(diff) > 0.01 && op.subItems.length > 0) {
                                                                                        op.subItems[0].price = parseFloat((op.subItems[0].price + diff).toFixed(2))
                                                                                    }

                                                                                    return { ...op }
                                                                                }
                                                                                return op
                                                                            })

                                                                            setAppointment({
                                                                                ...appointment,
                                                                                appointmentOperations: updatedOperations
                                                                            })
                                                                        } else {
                                                                            updateOperationData(row.id, 'price', newPrice)
                                                                        }
                                                                    }}

                                                                />
                                                                <table>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Price')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <Typography.Text strong>
                                                                                {Helper.Currency.format(row?.operation?.price)}
                                                                            </Typography.Text>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Institution')} / {t('Special')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <div style={{ width: '100%', display: 'flex', gap: 4 }}>
                                                                                <Typography.Text>
                                                                                    {Helper.Currency.format(getInstitutionPrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                                {' / '}
                                                                                <Typography.Text>
                                                                                    {Helper.Currency.format(getAppointmentTypePrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </table>
                                                            </div>
                                                        )
                                                    }
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: '#',
                                                    width: 50,
                                                    render: (text, row) => (
                                                        <Button
                                                            size={'small'}
                                                            danger
                                                            disabled={
                                                                Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                                            }
                                                            onClick={() => {
                                                                setAppointment({
                                                                    ...appointment,
                                                                    appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== row.id),
                                                                })
                                                            }}>
                                                            X
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                            summary={() => getPaymentSummary(4)}
                                        />
                                    )}
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                key={'extra'}
                                tab={t('Extra')}
                            >
                                <>
                                    <Form
                                        size={'small'}
                                        ref={formOtherOperationRef}
                                        layout={!screens.md ? 'vertical' : 'inline'}
                                        onFinish={onOtherOperationFinish}
                                    >
                                        <Form.Item
                                            name={'name'}
                                            rules={[{ required: true }]}
                                            style={!screens.md ? { marginBottom: 8 } : {}}
                                        >
                                            <Input placeholder={t('Name')} />
                                        </Form.Item>
                                        <Form.Item
                                            name={'price'}
                                            rules={[{ required: true, message: '' }]}
                                            style={!screens.md ? { marginBottom: 8 } : {}}
                                        >
                                            <InputNumber
                                                style={!screens.md ? { width: '100%' } : { minWidth: 200 }}
                                                placeholder={t('Price')}
                                                min={0}
                                                step={0.01}
                                                prefix={Currency[currency]}
                                            />
                                        </Form.Item>
                                        <Form.Item style={!screens.md ? { marginBottom: 8 } : {}}>
                                            <Button
                                                className={'bg-warning'}
                                                htmlType={'submit'}
                                                style={!screens.md ? { width: '100%' } : {}}
                                            >
                                                +
                                            </Button>
                                        </Form.Item>
                                    </Form>

                                    {!screens.md ? renderMobileExtraOperations() : (
                                        <Table
                                            size={'small'}
                                            rowKey={'id'}
                                            dataSource={appointment.appointmentOtherOperations}
                                            pagination={false}
                                            columns={[
                                                { key: 'name', dataIndex: 'name', title: t('Name') },
                                                {
                                                    key: 'price',
                                                    dataIndex: 'price',
                                                    title: t('Price'),
                                                    render: (text, row) => Helper.Currency.format(text, { currency })
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: t('#'),
                                                    render: (text, row) => <Button size={'small'}
                                                        onClick={() => deleteOtherOperation(row)}
                                                        danger={true}>X</Button>
                                                },
                                            ]}
                                            summary={() => getPaymentSummary(3)}
                                        />
                                    )}
                                </>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>

                </div>
            </Card>
            <div dangerouslySetInnerHTML={{ __html: appointment.institution.note ?? '------' }} style={{ marginTop: 8 }} />

            {!screens.md && (
                <div style={{ display: 'flex', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
                    {renderActionButtons()}
                </div>
            )}
        </Card>
    )
}

const TitleCurrencySelector = ({ value, onChange, disabled }) => {
    const [val, setVal] = useState(value)

    useEffect(() => {
        onChange(val)
    }, [val]);

    useEffect(() => {
        if (value !== val) {
            setVal(value)
        }
    }, [value]);

    return (
        <CurrencySelect
            placeholder={''}
            style={{ width: 100 }}
            disabled={disabled}
            size={'small'}
            value={val}
            onChange={(e) => setVal(e)}
        />
    )
}